import { AccountAvatar, getInitials } from '@nexus/ari-lib/components/account-avatar';
import { solidCard, solidCardHover } from '@nexus/ari-lib/utils/glass-styles';
import { motion as motionTokens } from '@nexus/ari-lib/theme/motion';
import { Card, Stack, Divider, Typography, Box } from '@mui/material';
import { WorkspaceSwitcher } from '../../components/workspace-switcher';

type DashboardAccountSectionProps = {
  name?: string;
  email: string;
  picture?: string;
  onClick?: () => void;
  'data-testid'?: string;
};

export function DashboardAccountSection({
  name,
  email,
  picture,
  onClick,
  'data-testid': testId = 'cloud.dashboard.account-section',
}: DashboardAccountSectionProps) {
  const displayName = name?.trim() || email.split('@')[0];

  return (
    <Card
      data-testid={testId}
      onClick={onClick}
      sx={{
        height: '100%',
        borderRadius: 1.5,
        ...solidCard,
        ...(onClick && {
          cursor: 'pointer',
          transition: `background-color ${motionTokens.duration.fast} ${motionTokens.ease.out}`,
          '&:hover': solidCardHover,
        }),
      }}
    >
      <Stack
        spacing={1.5}
        sx={{
          height: '100%',
          px: 3,
          py: 3,
          // Anchor to the top so the avatar/name stay put when the account
          // group's height changes between personal and business variants.
          justifyContent: 'flex-start',
        }}
      >
        {/* Profile group — who you are */}
        <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
          <AccountAvatar src={picture} alt={displayName} sx={{ width: 80, height: 80, fontSize: 28, mb: 1 }}>
            {getInitials(name, email)}
          </AccountAvatar>

          <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ maxWidth: '100%' }}>
            {displayName}
          </Typography>

          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '100%' }}>
            {email}
          </Typography>
        </Stack>

        <Divider sx={{ borderColor: 'grey.800' }} />

        {/* Workspace group - which workspace the app is scoped to */}
        <Stack spacing={0.75} sx={{ width: '100%', alignItems: 'flex-start' }}>
          <Box sx={{ width: '100%' }} onClick={(e) => e.stopPropagation()}>
            <WorkspaceSwitcher />
          </Box>
        </Stack>
      </Stack>
    </Card>
  );
}
